/**
 * Safely parses a datetime string or Date representing Central Time (America/Chicago / CST / CDT)
 * into a valid JavaScript Date object representing the exact moment in time across all timezones.
 */
export function parseCSTDate(input: string | Date | null | undefined): Date | null {
    if (!input) return null;
    if (input instanceof Date) return isNaN(input.getTime()) ? null : input;

    let str = String(input).trim();
    if (!str) return null;

    // If it's just a date like YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        str = `${str} 23:59:59`;
    }

    // Clean up format YYYY-MM-DD HH:mm(:ss) or YYYY-MM-DDTHH:mm(:ss)
    const normalized = str.replace(' ', 'T');
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!match) {
        const d = new Date(str);
        return isNaN(d.getTime()) ? null : d;
    }

    // If already contains an explicit timezone offset like -05:00, -06:00, or Z
    if (str.includes('Z') || /[+-]\d{2}:\d{2}$/.test(str)) {
        const d = new Date(normalized);
        return isNaN(d.getTime()) ? null : d;
    }

    const [, yearStr, monthStr, dayStr, hourStr, minStr, secStr] = match;
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const day = parseInt(dayStr, 10);
    const hour = parseInt(hourStr, 10);
    const min = parseInt(minStr, 10);
    const sec = secStr ? parseInt(secStr, 10) : 0;

    // Determine America/Chicago offset for this specific target date
    const approxUtc = new Date(Date.UTC(year, month - 1, day, hour, min, sec));
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false
    });

    const parts = formatter.formatToParts(approxUtc);
    const getPart = (t: string) => parseInt(parts.find(p => p.type === t)?.value || '0', 10);
    let pYear = getPart('year');
    let pMonth = getPart('month');
    let pDay = getPart('day');
    let pHour = getPart('hour');
    if (pHour === 24) pHour = 0;
    let pMin = getPart('minute');
    let pSec = getPart('second');

    const formattedAsChicagoUtc = Date.UTC(pYear, pMonth - 1, pDay, pHour, pMin, pSec);
    const offsetMs = formattedAsChicagoUtc - approxUtc.getTime();

    const targetLocalEpoch = Date.UTC(year, month - 1, day, hour, min, sec);
    const exactEpoch = targetLocalEpoch - offsetMs;

    const result = new Date(exactEpoch);
    return isNaN(result.getTime()) ? null : result;
}

/**
 * Formats any date into a human-readable Central Time (America/Chicago / CST) string.
 */
export function formatCSTDateDisplay(date: Date | string | null | undefined): string {
    const d = parseCSTDate(date);
    if (!d) return '';
    return d.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
    });
}

/**
 * Formats a date for HTML5 datetime-local inputs in Central Time (YYYY-MM-DDTHH:mm).
 */
export function formatForDateTimeLocal(date: Date | string | null | undefined): string {
    const d = parseCSTDate(date);
    if (!d) return '';

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
    const parts = formatter.formatToParts(d);
    const getPart = (type: string) => parts.find(p => p.type === type)?.value || '';
    let hour = getPart('hour');
    if (hour === '24') hour = '00';
    return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${hour}:${getPart('minute')}`;
}

// Authoritative Default 50% Installment Payment Access Deadlines by Track:
// Foundation Track Deadline: Monday, September 21, 2026 11:59:59 PM CST/CDT
export const FOUNDATION_PAYMENT_DEADLINE = parseCSTDate('2026-09-21 23:59:59') || new Date('2026-09-21T23:59:59-05:00');
export const FOUNDATION_PAYMENT_DEADLINE_DISPLAY = formatCSTDateDisplay(FOUNDATION_PAYMENT_DEADLINE) || 'Monday, September 21, 2026 at 11:59 PM CDT';

// Cohort 12 Deadline: Monday, September 21, 2026 11:59:59 PM CST/CDT
export const COHORT_12_PAYMENT_DEADLINE = parseCSTDate('2026-09-21 23:59:59') || new Date('2026-09-21T23:59:59-05:00');
export const COHORT_12_PAYMENT_DEADLINE_DISPLAY = formatCSTDateDisplay(COHORT_12_PAYMENT_DEADLINE) || 'Monday, September 21, 2026 at 11:59 PM CDT';

// Cohort 13 / Previous Professional Track Deadline: Thursday, October 8, 2026 11:59:59 PM CST/CDT
export const COHORT_13_PAYMENT_DEADLINE = parseCSTDate('2026-10-08 23:59:59') || new Date('2026-10-08T23:59:59-05:00');
export const COHORT_13_PAYMENT_DEADLINE_DISPLAY = formatCSTDateDisplay(COHORT_13_PAYMENT_DEADLINE) || 'Thursday, October 8, 2026 at 11:59 PM CDT';

// Cohort 14 / Project Scheduling & Controls Track Deadline: Monday, November 23, 2026 11:59:59 PM CST
export const COHORT_14_PAYMENT_DEADLINE = parseCSTDate('2026-11-23 23:59:59') || new Date('2026-11-23T23:59:59-06:00');
export const COHORT_14_PAYMENT_DEADLINE_DISPLAY = formatCSTDateDisplay(COHORT_14_PAYMENT_DEADLINE) || 'Monday, November 23, 2026 at 11:59 PM CST';

// Bundle Track Deadline: Monday, September 21, 2026 11:59:59 PM CST/CDT
export const BUNDLE_PAYMENT_DEADLINE = parseCSTDate('2026-09-21 23:59:59') || new Date('2026-09-21T23:59:59-05:00');
export const BUNDLE_PAYMENT_DEADLINE_DISPLAY = formatCSTDateDisplay(BUNDLE_PAYMENT_DEADLINE) || 'Monday, September 21, 2026 at 11:59 PM CDT';

export const DEFAULT_PAYMENT_DEADLINE = COHORT_14_PAYMENT_DEADLINE;
export const DEFAULT_PAYMENT_DEADLINE_DISPLAY = COHORT_14_PAYMENT_DEADLINE_DISPLAY;

export interface PaymentInfo {
    isFullyPaid: boolean;
    hasCohort: boolean;
    hasStarted: boolean;
    isExpired: boolean;
    paymentPlan: 'full' | '50_percent';
    amountPaid: number;
    totalAmount: number;
    remainingBalance: number;
    courseType: 'foundation' | 'professional' | 'bundle';
    courseTitle: string;
    cohortName: string;
    startDate: Date | null;
    deadlineDate: Date;
    deadlineDisplay: string;
    durationDays: number;
    daysRemaining: number;
    hoursRemaining: number;
    minutesRemaining: number;
    secondsRemaining: number;
    bannerLevel: 'none' | 'yellow' | 'orange' | 'red' | 'expired';
    paymentLink?: string;
    customPaymentAmount?: number | null;
    customPaymentDeadline?: string | null;
    isCustomDeadline?: boolean;
}

export function getPaymentInfo(user: any, cohort: any): PaymentInfo {
    // 1. Determine Payment Plan
    const rawPlan = (user?.payment_plan || cohort?.pivot?.payment_plan || '').toString().toLowerCase();

    const isFiftyPercent = (
        rawPlan === 'installment' || 
        rawPlan === '50_percent' || 
        rawPlan === '50%' || 
        rawPlan === 'partial' || 
        rawPlan === 'half'
    );

    // The payment tracking banner/lock should ONLY show when the instructor explicitly makes it active or student chose 50% plan
    const isExplicitlyActivated = 
        user?.payment_tracking_enabled === true || 
        user?.payment_tracking_enabled === 1 || 
        user?.payment_tracking_enabled === '1' ||
        cohort?.payment_tracking_enabled === true || 
        cohort?.payment_tracking_enabled === 1 || 
        cohort?.payment_tracking_enabled === '1' ||
        cohort?.pivot?.payment_tracking_enabled === true || 
        cohort?.pivot?.payment_tracking_enabled === 1 ||
        isFiftyPercent;

    // Course Title & Type Resolution
    const candidateStrings: string[] = [];
    if (user?.course_name) candidateStrings.push(String(user.course_name));
    if (user?.course_title) candidateStrings.push(String(user.course_title));
    if (user?.track) candidateStrings.push(String(user.track));
    if (user?.course) candidateStrings.push(typeof user.course === 'string' ? user.course : (user.course?.title || user.course?.name || ''));
    if (cohort?.course?.title) candidateStrings.push(String(cohort.course.title));
    if (cohort?.course?.name) candidateStrings.push(String(cohort.course.name));
    if (cohort?.name) candidateStrings.push(String(cohort.name));
    if (cohort?.pivot?.course_name) candidateStrings.push(String(cohort.pivot.course_name));
    if (user?.payment_plan) candidateStrings.push(String(user.payment_plan));

    if (Array.isArray(user?.cohorts)) {
        user.cohorts.forEach((c: any) => {
            if (c?.name) candidateStrings.push(String(c.name));
            if (c?.course?.title) candidateStrings.push(String(c.course.title));
            if (c?.course?.name) candidateStrings.push(String(c.course.name));
        });
    }

    const combinedSearchText = candidateStrings.join(' ').toLowerCase();

    // 0. Extract explicit cohort signals
    const cohortIdStr = cohort?.id !== undefined && cohort?.id !== null ? String(cohort.id).toLowerCase() : '';
    const cohortNameStr = cohort?.name ? String(cohort.name).toLowerCase() : '';
    const userCohorts = Array.isArray(user?.cohorts) ? user.cohorts : [];
    const hasCohort14InCohorts = userCohorts.some((c: any) => {
        const cId = c?.id !== undefined && c?.id !== null ? String(c.id).toLowerCase() : '';
        const cName = c?.name ? String(c.name).toLowerCase() : '';
        return cId === '14' || cId.includes('14') || cName.includes('14');
    });

    const isDirectCohort14 = 
        cohortIdStr === '14' ||
        cohortIdStr.includes('14') ||
        cohortNameStr.includes('14') ||
        hasCohort14InCohorts;

    // 1. Check if student is explicitly on Cohort 14 / Cohort 14.0 / Cohort 14.1 / Recordings
    const isCohort14_0 = combinedSearchText.includes('14.0') || combinedSearchText.includes('14-0') || combinedSearchText.includes('14_0');
    const isCohort14_1 = combinedSearchText.includes('14.1') || combinedSearchText.includes('14-1') || combinedSearchText.includes('14_1');
    let isCohort14 = 
        isDirectCohort14 ||
        isCohort14_0 ||
        isCohort14_1 ||
        combinedSearchText.includes('cohort 14') ||
        combinedSearchText.includes('cohort-14') ||
        combinedSearchText.includes('cohort14') ||
        combinedSearchText.includes('cohort 14 recordings') ||
        (combinedSearchText.includes('14') && !combinedSearchText.includes('bundle'));

    // 2. Check if student is explicitly on Cohort 13 / Cohort 13 Recordings
    const isCohort13 = !isCohort14 && (
        combinedSearchText.includes('cohort 13') ||
        combinedSearchText.includes('cohort-13') ||
        combinedSearchText.includes('cohort13') ||
        combinedSearchText.includes('cohort 13 recordings') ||
        (combinedSearchText.includes('13') && !combinedSearchText.includes('bundle'))
    );

    // 3. Check if student is on Cohort 12 (Cohort 12 is past All-in-One Dual Bundle)
    // NOTE: If student has explicit Cohort 14 enrollment, do not let an unedited course_name default treat them as Cohort 12!
    const isCohort12 = !isCohort14 && !isCohort13 && (
        combinedSearchText.includes('cohort 12') ||
        combinedSearchText.includes('cohort-12') ||
        combinedSearchText.includes('cohort12') ||
        combinedSearchText.includes('cohort 12 recordings') ||
        (combinedSearchText.includes('12') && !combinedSearchText.includes('bundle'))
    );

    // 4. Check if student is on the All-in-One Dual Bundle
    const isBundle = !isCohort14 && (isCohort12 || (!isCohort13 && (
        combinedSearchText.includes('bundle') || 
        combinedSearchText.includes('all-in-one') || 
        combinedSearchText.includes('all in one') || 
        combinedSearchText.includes('all in 1') || 
        combinedSearchText.includes('all-in-1') || 
        combinedSearchText.includes('dual') ||
        (combinedSearchText.includes('foundation') && (combinedSearchText.includes('professional') || combinedSearchText.includes('master')))
    )));

    // If not Cohort 13, Cohort 12, or Bundle, any active Project Scheduling / Controls track is Cohort 14
    if (!isCohort12 && !isCohort13 && !isBundle && (
        combinedSearchText.includes('scheduling') || 
        combinedSearchText.includes('controls') || 
        combinedSearchText.includes('project controls')
    )) {
        isCohort14 = true;
    }

    const isPro = isCohort14 || isCohort13 || (!isBundle && (
        combinedSearchText.includes('professional') || 
        combinedSearchText.includes('master') || 
        combinedSearchText.includes('scheduling') || 
        combinedSearchText.includes('controls')
    ));

    const courseType: 'foundation' | 'professional' | 'bundle' = isBundle ? 'bundle' : (isPro ? 'professional' : 'foundation');
    const durationDays = isBundle ? 21 : 14;

    // Determine clean, human-readable course title - strictly honor user's assigned course/cohort name
    let courseTitle = user?.course_name 
        || cohort?.name 
        || cohort?.course?.title 
        || cohort?.course?.name 
        || cohort?.pivot?.course_name;

    if (!courseTitle) {
        courseTitle = isCohort14
            ? 'Project Scheduling Training • Cohort 14'
            : (isCohort13 
                ? 'Project Scheduling Training • Cohort 13' 
                : (isBundle 
                    ? 'All-in-One Dual Masterclass Bundle' 
                    : 'Project Scheduling & Controls Training'));
    }

    // Explicit dynamic overrides from user / cohort records if available
    const explicitTotal = Number(user?.total_amount || user?.course_price || cohort?.pivot?.total_amount || cohort?.course?.price);
    const explicitRemaining = Number(user?.remaining_balance ?? user?.balance_due ?? cohort?.pivot?.remaining_balance);
    const explicitPaid = Number(user?.amount_paid || cohort?.pivot?.amount_paid);

    // Custom payment amount set by admin/instructor override
    const customPaymentAmount = (user?.custom_payment_amount !== null && user?.custom_payment_amount !== undefined && !isNaN(Number(user?.custom_payment_amount)) && Number(user?.custom_payment_amount) > 0)
        ? Number(user?.custom_payment_amount)
        : (cohort?.pivot?.custom_payment_amount !== null && cohort?.pivot?.custom_payment_amount !== undefined && !isNaN(Number(cohort?.pivot?.custom_payment_amount)) && Number(cohort?.pivot?.custom_payment_amount) > 0)
            ? Number(cohort?.pivot?.custom_payment_amount)
            : null;

    // Standard actual pricing by cohort track:
    // - Cohort 14 = $999 (50% = $499.99, rem = $499.99)
    // - Cohort 13 = $1,199 / $1,200 (50% = $600, rem = $600)
    // - Cohort 12 / All-in-One Dual Masterclass Bundle = $1,799 / $1,800 (50% = $900, rem = $900)
    // - Foundation = $799 (50% = $400, rem = $399)
    const standardPricing = {
        cohort14: { total: 999, installment: 499.99, remaining: 499.99 },
        cohort13: { total: 1199, installment: 600, remaining: 600 },
        cohort12: { total: 1799, installment: 900, remaining: 900 },
        foundation: { total: 799, installment: 400, remaining: 399 },
        bundle: { total: 1799, installment: 900, remaining: 900 }
    };

    const currentConfig = isCohort14
        ? standardPricing.cohort14
        : ((isBundle || isCohort12) 
            ? standardPricing.bundle 
            : (isCohort13 
                ? standardPricing.cohort13 
                : (isPro ? standardPricing.cohort14 : standardPricing.foundation)));

    const defaultRemaining = customPaymentAmount !== null ? customPaymentAmount : currentConfig.remaining;
    const defaultInstallment = customPaymentAmount !== null ? customPaymentAmount : currentConfig.installment;
    const defaultTotal = customPaymentAmount !== null ? (customPaymentAmount * 2) : currentConfig.total;

    const totalAmount = (!isNaN(explicitTotal) && explicitTotal > 0) ? explicitTotal : defaultTotal;
    const remainingBalance = isFiftyPercent
        ? ((!isNaN(explicitRemaining) && explicitRemaining >= 0) ? explicitRemaining : defaultRemaining)
        : 0;
    const amountPaid = isFiftyPercent
        ? ((!isNaN(explicitPaid) && explicitPaid >= 0) ? explicitPaid : defaultInstallment)
        : totalAmount;

    // Resolve Deadline Date based on Track:
    // - Cohort 14: Nov 23, 2026 (11:59 PM CST)
    // - Cohort 13: Oct 8, 2026 (11:59 PM CDT)
    // - Cohort 12: Sept 21, 2026 (11:59 PM CDT)
    // - Bundle: Sept 21, 2026 (11:59 PM CDT)
    // - Default: Nov 23, 2026 (COHORT_14_PAYMENT_DEADLINE)
    const defaultTrackDeadline = isCohort14
        ? COHORT_14_PAYMENT_DEADLINE
        : (isCohort13
            ? COHORT_13_PAYMENT_DEADLINE
            : (isCohort12
                ? COHORT_12_PAYMENT_DEADLINE
                : (isBundle
                    ? BUNDLE_PAYMENT_DEADLINE
                    : COHORT_14_PAYMENT_DEADLINE)));

    const defaultTrackDeadlineDisplay = isCohort14
        ? COHORT_14_PAYMENT_DEADLINE_DISPLAY
        : (isCohort13
            ? COHORT_13_PAYMENT_DEADLINE_DISPLAY
            : (isCohort12
                ? COHORT_12_PAYMENT_DEADLINE_DISPLAY
                : (isBundle
                    ? BUNDLE_PAYMENT_DEADLINE_DISPLAY
                    : COHORT_14_PAYMENT_DEADLINE_DISPLAY)));

    let deadlineDate: Date = defaultTrackDeadline;
    let deadlineDisplay = defaultTrackDeadlineDisplay;
    let isCustomDeadline = false;
    let customPaymentDeadline: string | null = null;

    if (user?.payment_deadline) {
        const parsed = parseCSTDate(user.payment_deadline);
        if (parsed) {
            deadlineDate = parsed;
            deadlineDisplay = formatCSTDateDisplay(parsed);
            isCustomDeadline = true;
            customPaymentDeadline = String(user.payment_deadline);
        }
    } else if (cohort?.payment_deadline || cohort?.pivot?.payment_deadline) {
        const parsed = parseCSTDate(cohort?.payment_deadline || cohort?.pivot?.payment_deadline);
        if (parsed) {
            deadlineDate = parsed;
            deadlineDisplay = formatCSTDateDisplay(parsed);
            isCustomDeadline = true;
            customPaymentDeadline = String(cohort?.payment_deadline || cohort?.pivot?.payment_deadline);
        }
    }

    const hasCohort = !!cohort;
    const rawStartDate = cohort?.start_date || cohort?.pivot?.created_at;
    let startDate: Date | null = null;

    if (rawStartDate) {
        startDate = new Date(rawStartDate);
        if (isNaN(startDate.getTime())) {
            startDate = new Date();
        }
    }

    // Dynamic payment link: Custom link if set by admin, otherwise automatic flow ($900 link for Bundle / Cohort 12, $600 for Cohort 13, $499.99 for Cohort 14)
    const defaultPaymentLink = (isBundle || isCohort12)
        ? 'https://square.link/u/em8IM5hW' // $900 USD 50% Bundle / Cohort 12 Link
        : (isCohort13
            ? 'https://square.link/u/HjJ7Mn7U' // $600 USD 50% Cohort 13 Link
            : 'https://square.link/u/WOMQtaL3'); // $499.99 USD 50% Cohort 14 Link

    const customPaymentLink = (typeof user?.custom_payment_link === 'string' && user.custom_payment_link.trim() !== '')
        ? user.custom_payment_link.trim()
        : (typeof cohort?.pivot?.custom_payment_link === 'string' && cohort.pivot.custom_payment_link.trim() !== ''
            ? cohort.pivot.custom_payment_link.trim()
            : null);

    const paymentLink = customPaymentLink || defaultPaymentLink;
    const hasStarted = true;

    const cohortName = cohort?.name 
        || (Array.isArray(user?.cohorts) && user.cohorts[0]?.name ? user.cohorts[0].name : '')
        || user?.course_name 
        || 'Assigned Cohort';

    // If NOT explicitly activated by instructor AND not 50% plan, treat as fully paid with no banner or lock
    if (!isExplicitlyActivated) {
        return {
            isFullyPaid: true,
            hasCohort,
            hasStarted,
            isExpired: false,
            paymentPlan: 'full',
            amountPaid: totalAmount,
            totalAmount: totalAmount,
            remainingBalance: 0,
            courseType,
            courseTitle,
            cohortName,
            startDate: cohort?.start_date ? new Date(cohort.start_date) : null,
            deadlineDate,
            deadlineDisplay,
            durationDays,
            daysRemaining: 0,
            hoursRemaining: 0,
            minutesRemaining: 0,
            secondsRemaining: 0,
            bannerLevel: 'none',
            paymentLink,
            customPaymentAmount,
            customPaymentDeadline,
            isCustomDeadline,
        };
    }

    const isFullyPaid = !isFiftyPercent && (rawPlan === 'full' || rawPlan === 'fully_paid' || rawPlan === '100%' || rawPlan === 'completed' || user?.payment_status === 'approved');

    const now = new Date();

    let isExpired = false;
    let daysRemaining = 0;
    let hoursRemaining = 0;
    let minutesRemaining = 0;
    let secondsRemaining = 0;
    let bannerLevel: 'none' | 'yellow' | 'orange' | 'red' | 'expired' = 'none';

    if (isFullyPaid) {
        bannerLevel = 'none';
    } else {
        const diffMs = deadlineDate.getTime() - now.getTime();
        const totalSecs = Math.floor(diffMs / 1000);

        if (totalSecs <= 0) {
            isExpired = true;
            bannerLevel = 'expired';
            daysRemaining = 0;
            hoursRemaining = 0;
            minutesRemaining = 0;
            secondsRemaining = 0;
        } else {
            daysRemaining = Math.floor(totalSecs / 86400);
            hoursRemaining = Math.floor((totalSecs % 86400) / 3600);
            minutesRemaining = Math.floor((totalSecs % 3600) / 60);
            secondsRemaining = totalSecs % 60;

            const totalHoursLeft = diffMs / (1000 * 60 * 60);

            if (totalHoursLeft <= 48) {
                bannerLevel = 'red';
            } else if (totalHoursLeft <= 96) {
                bannerLevel = 'orange';
            } else {
                bannerLevel = 'yellow';
            }
        }
    }

    return {
        isFullyPaid,
        hasCohort,
        hasStarted,
        isExpired,
        paymentPlan: isFiftyPercent ? '50_percent' : 'full',
        amountPaid,
        totalAmount,
        remainingBalance: isFullyPaid ? 0 : remainingBalance,
        courseType,
        courseTitle,
        cohortName,
        startDate,
        deadlineDate,
        deadlineDisplay,
        durationDays,
        daysRemaining,
        hoursRemaining,
        minutesRemaining,
        secondsRemaining,
        bannerLevel,
        paymentLink,
        customPaymentAmount,
        customPaymentDeadline,
        isCustomDeadline,
    };
}
